import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import { ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-react';
import { CloneQuoteData, type QuoteData } from '@iqd/shared-quoting/core';
import { useBudgetSession } from '../use-budget-session';
import { useDomainTracking } from '../../../shared/hooks/use-domain-tracking';
import { Alert } from '../../../shared/components/alert';
import { Button } from '../../../shared/components/button';
import { Spinner } from '../../../shared/components/spinner';
import { cn } from '../../../shared/utils/cn';
import { trackEvent } from '../../../shared/utils/analytics';
import { LivePreview2D } from '../live-preview-2d';
import { LOAD_TYPES_QUERY, type LoadTypesQueryResult } from '../graphql';
import { step3Schema, wizardFormSchema, type WizardFormValues } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProgressIndicator } from './progress-indicator';
import { STEP_TITLES, useWizard, type StepIndex } from './use-wizard';
import { Step1FloorSize } from './step-1-floor-size';
import { ALLOWED_LOAD_TYPES, Step2Purpose } from './step-2-purpose';
import { Step3YourDetails } from './step-3-your-details';
import { Step4Result } from './step-4-result';

function applyFireProtection(qd: QuoteData, fireRated: boolean): void {
  qd.GeneralData.IsFireRated = fireRated;
  qd.FireRatingData.CostFireRating = fireRated;
  qd.FireRatingData.CostCeilings = fireRated;
  qd.FireRatingData.CostFascia = fireRated;
  qd.FireRatingData.CostColumnCasings = fireRated;
}

function updateAncillaries(qd: QuoteData): void {
  for (const ancillary of qd.AncillaryData.AncillaryArguments) {
    if (ancillary.Height > 0) {
      ancillary.Height = qd.FloorData.TiersData.TierData[0].Height;
    } else if (ancillary.Length > 0) {
      ancillary.Length = qd.FloorData.GridData.XDim * 2 + qd.FloorData.GridData.YDim * 2;
    }
  }
}

function buildQuoteData(base: QuoteData, value: WizardFormValues): QuoteData {
  const qd = CloneQuoteData(base);
  qd.GeneralData.AreaCode = value.siteAreaCodeId ?? null;
  qd.FloorData.GridData.XDim = Number(value.length);
  qd.FloorData.GridData.YDim = Number(value.width);
  qd.FloorData.TiersData.TierData[0].LoadType =
    value.loadType === '' || value.loadType == null ? null : Number(value.loadType);
  qd.FloorData.TiersData.TierData[0].Height = Number(value.height);
  qd.FloorData.TiersData.TierData[0].CutoutData = [];
  qd.FloorData.TiersData.TierCount = 1;
  applyFireProtection(qd, !!value.fireProtection);
  updateAncillaries(qd);
  return qd;
}

function valuesFromQuoteData(quoteData: QuoteData): WizardFormValues {
  // `site` / `exWorks` start empty — Step1 reconciles them from `siteAreaCodeId`
  // once the area-codes list loads.
  return {
    site: '',
    exWorks: false,
    siteAreaCodeId: quoteData.GeneralData.AreaCode ?? null,
    length: quoteData.FloorData.GridData.XDim,
    width: quoteData.FloorData.GridData.YDim,
    loadType: quoteData.FloorData.TiersData.TierData[0].LoadType ?? '',
    height: quoteData.FloorData.TiersData.TierData[0].Height,
    fireProtection: quoteData.GeneralData.IsFireRated,
    phoneNumber: '',
    email: '',
    turnstile: null,
  };
}

export function WizardShell() {
  const session = useBudgetSession();
  const { info } = useDomainTracking();
  const { data: loadTypesData } = useQuery<LoadTypesQueryResult>(LOAD_TYPES_QUERY);

  return session.quoteData ? (
    <WizardShellInner
      session={session}
      info={info}
      loadTypeRows={loadTypesData?.loadTypeDefaults ?? []}
    />
  ) : (
    <div className="mx-auto max-w-7xl px-4 py-16 flex justify-center">
      <Spinner />
    </div>
  );
}

interface InnerProps {
  session: ReturnType<typeof useBudgetSession>;
  info: ReturnType<typeof useDomainTracking>['info'];
  loadTypeRows: Array<{ id: number; description: string }>;
}

function WizardShellInner({ session, info, loadTypeRows }: InnerProps) {
  const initialQd = session.quoteData as QuoteData;
  const methods = useForm<WizardFormValues>({
    defaultValues: valuesFromQuoteData(initialQd),
    resolver: zodResolver(wizardFormSchema),
    mode: 'onChange',
  });

  const [submitError, setSubmitError] = useState<string | null>(null);
  const lastInitialQdRef = useRef<QuoteData>(initialQd);

  const onLastStepBeforeResult = useCallback(async (): Promise<boolean> => {
    setSubmitError(null);
    const values = methods.getValues();
    const qd = buildQuoteData(lastInitialQdRef.current, values);

    // Floor spec only — never the email or phone the customer just entered.
    const specParams = {
      floor_length: Number(values.length),
      floor_width: Number(values.width),
      load_type: values.loadType === '' || values.loadType == null ? null : Number(values.loadType),
      fire_protection: !!values.fireProtection,
    };
    trackEvent('budget_calc_lead_submit', specParams);

    try {
      const res = await session.generateAndAttachContact(
        qd,
        { email: values.email ?? '', phoneNumber: values.phoneNumber ?? '' },
        {
          embeddingDomain: info.parentDomain,
          isEmbedded: info.isEmbedded,
          referrerUrl: document.referrer || window.location.href,
        },
      );
      if (!res) return false;

      const total = (res.backsheet as { BudgetTotal?: number } | null)?.BudgetTotal;
      trackEvent('budget_calc_quote_generated', {
        ...specParams,
        value: typeof total === 'number' ? total : null,
        currency: 'GBP',
      });
      return true;
    } catch (err) {
      const message = (err as Error).message;
      setSubmitError(message);
      trackEvent('budget_calc_quote_failed', specParams);
      return false;
    }
  }, [methods, session, info]);

  const wizard = useWizard(methods, onLastStepBeforeResult);

  useEffect(() => {
    trackEvent('budget_calc_step', {
      step_index: wizard.currentStep + 1,
      step_name: STEP_TITLES[wizard.currentStep as StepIndex],
    });
  }, [wizard.currentStep]);

  useEffect(() => {
    if (lastInitialQdRef.current === initialQd) return;
    lastInitialQdRef.current = initialQd;
    methods.reset(valuesFromQuoteData(initialQd));
  }, [initialQd, methods]);

  const loadTypeId = useWatch({ control: methods.control, name: 'loadType' });
  const loadTypeLabel = useMemo(() => {
    if (loadTypeId == null || loadTypeId === '') return null;
    const row = loadTypeRows.find((lt) => lt.id === Number(loadTypeId));
    return row && ALLOWED_LOAD_TYPES.has(row.description) ? row.description : null;
  }, [loadTypeId, loadTypeRows]);

  // Step 3 button gating runs the schema against watched values directly
  // because untouched fields don't populate `formState.errors` on `onChange`
  // mode, so a fresh step 3 would briefly show the button enabled. The watch
  // is gated to step 2 so earlier steps don't re-render on these fields.
  const onStep3 = wizard.currentStep === 2;
  const [emailValue, phoneValue, turnstileValue] = useWatch({
    control: methods.control,
    name: ['email', 'phoneNumber', 'turnstile'],
    disabled: !onStep3,
  });
  const canSubmitContact = useMemo(
    () =>
      onStep3 &&
      step3Schema.safeParse({
        email: emailValue,
        phoneNumber: phoneValue,
        turnstile: turnstileValue,
      }).success,
    [onStep3, emailValue, phoneValue, turnstileValue],
  );

  const handleStartOver = useCallback(async () => {
    setSubmitError(null);
    // The wizard is only sent back to step 1 once the new quote data is in.
    // Resetting both at once showed step 1 seeded from the previous quote, and
    // overwrote anything typed before the new one arrived. A failure leaves the
    // quote they already have rather than stranding them on an empty step 1.
    try {
      await session.reset();
    } catch (err) {
      setSubmitError((err as Error).message);
      return;
    }
    wizard.reset();
  }, [session, wizard]);

  const handleNext = useCallback(async () => {
    await wizard.goNext();
  }, [wizard]);

  let nextLabel = 'Next';
  if (onStep3) nextLabel = session.generating ? 'Generating…' : 'Get my budget';
  const buttonRowClass =
    wizard.currentStep === 0
      ? 'flex justify-end'
      : 'grid grid-cols-2 gap-3 lg:flex lg:items-center lg:justify-between';

  return (
    <FormProvider {...methods}>
      <div className="mx-auto max-w-7xl px-3 sm:px-6 py-4 sm:py-6" data-testid="wizard">
        <ProgressIndicator currentStep={wizard.currentStep} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          {wizard.currentStep < 3 ? (
            <>
              <div className="lg:col-span-5">
                <div className="bg-white rounded-xl border border-hilevel-border shadow-sm">
                  <div
                    className="p-5 sm:p-6 lg:p-7"
                    data-testid={`wizard-step-${wizard.currentStep + 1}`}
                  >
                    {wizard.currentStep === 0 && <Step1FloorSize />}
                    {wizard.currentStep === 1 && <Step2Purpose />}
                    {wizard.currentStep === 2 && <Step3YourDetails />}

                    {submitError && (
                      <div className="mt-4">
                        <Alert data-testid="wizard-error">{submitError}</Alert>
                      </div>
                    )}

                    <div className={cn('mt-6 pt-5 border-t border-hilevel-border', buttonRowClass)}>
                      {wizard.currentStep > 0 && (
                        <Button
                          variant="secondary"
                          fullWidth
                          onClick={wizard.goBack}
                          data-testid="wizard-back"
                        >
                          <ChevronLeft className="text-xs" aria-hidden="true" />
                          Back
                        </Button>
                      )}
                      <Button
                        size="lg"
                        fullWidth
                        className="whitespace-nowrap"
                        onClick={handleNext}
                        disabled={session.generating || (onStep3 && !canSubmitContact)}
                        data-testid="wizard-next"
                      >
                        {onStep3 && session.generating && (
                          <LoaderCircle className="text-xs animate-spin" aria-hidden="true" />
                        )}
                        {nextLabel}
                        {wizard.currentStep < 2 && (
                          <ChevronRight className="text-xs" aria-hidden="true" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <LivePreview2D loadTypeLabel={loadTypeLabel} generating={session.generating} />
              </div>
            </>
          ) : (
            session.quoteData && (
              <div className="lg:col-span-12" data-testid="wizard-step-4">
                <Step4Result
                  quoteData={session.quoteData}
                  drawing={session.drawing}
                  backsheet={session.backsheet}
                  onStartOver={handleStartOver}
                />
              </div>
            )
          )}
        </div>
      </div>
    </FormProvider>
  );
}
